import {
  dashboardQuerySchema,
  isMembershipActive,
  targetMutationSchema,
  targetPeriodSchema,
  type DashboardAppMetric,
  type DashboardQuery,
  type TargetPeriod,
} from "@yahshi/contracts";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { DashboardApi } from "./DashboardApi";

type DemoOrder = {
  appId: string;
  amountMinor: number;
  createdAt: string;
  paidAt: string | null;
  refundedAt: string | null;
};
type DemoMembership = {
  appId: string;
  status: "active" | "expired" | "canceled";
  expiresAt: string;
  startedAt: string;
  canceledAt: string | null;
};

const demoApps = [
  { appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web" },
  { appId: "app_yahshi_admin", appName: "Yahshi Admin", appCode: "yahshi-admin" },
  { appId: "app_partner_sandbox", appName: "合作方沙箱", appCode: "partner-sandbox" },
] as const;

const demoOrders: DemoOrder[] = [
  { appId: "app_yahshi_web", amountMinor: 1990, createdAt: "2026-09-15T02:18:00.000Z", paidAt: null, refundedAt: null },
  { appId: "app_yahshi_web", amountMinor: 1990, createdAt: "2026-09-10T11:17:00.000Z", paidAt: "2026-09-10T11:18:00.000Z", refundedAt: null },
  { appId: "app_yahshi_web", amountMinor: 12800, createdAt: "2026-09-08T05:20:00.000Z", paidAt: "2026-09-08T05:28:00.000Z", refundedAt: null },
  { appId: "app_yahshi_admin", amountMinor: 600, createdAt: "2026-09-07T08:20:00.000Z", paidAt: "2026-09-07T08:22:00.000Z", refundedAt: "2026-09-09T03:40:00.000Z" },
  { appId: "app_yahshi_web", amountMinor: 1, createdAt: "2026-09-06T04:12:00.000Z", paidAt: null, refundedAt: null },
];

const demoMemberships: DemoMembership[] = [
  { appId: "app_yahshi_web", status: "active", expiresAt: "2026-10-15T02:18:00.000Z", startedAt: "2026-09-15T02:18:00.000Z", canceledAt: null },
  { appId: "app_yahshi_web", status: "expired", expiresAt: "2026-09-01T05:20:00.000Z", startedAt: "2025-09-08T05:20:00.000Z", canceledAt: null },
  { appId: "app_yahshi_admin", status: "canceled", expiresAt: "2026-12-01T08:20:00.000Z", startedAt: "2026-09-07T08:20:00.000Z", canceledAt: "2026-09-09T03:40:00.000Z" },
  { appId: "app_yahshi_web", status: "active", expiresAt: "2027-09-06T04:12:00.000Z", startedAt: "2026-09-06T04:12:00.000Z", canceledAt: null },
];

function shanghaiDay(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}

function inRange(iso: string | null, from: string, to: string): boolean {
  if (!iso) return false;
  const day = shanghaiDay(iso);
  return day >= from && day <= to;
}

function eachDay(from: string, to: string): string[] {
  const days: string[] = [];
  const cursor = new Date(`${from}T00:00:00Z`);
  const last = new Date(`${to}T00:00:00Z`);
  while (cursor <= last) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

export function summarizeDevelopmentCockpit(query: DashboardQuery, now = new Date()) {
  const { dateFrom: from, dateTo: to } = query;
  const apps: DashboardAppMetric[] = demoApps.map((app) => ({
    ...app,
    newUsers: 0,
    orderCount: demoOrders.filter((order) => order.appId === app.appId && inRange(order.createdAt, from, to)).length,
    paidAmountMinor: demoOrders
      .filter((order) => order.appId === app.appId && inRange(order.paidAt, from, to))
      .reduce((sum, order) => sum + order.amountMinor, 0),
    activeMembers: demoMemberships.filter((item) => item.appId === app.appId && isMembershipActive(item.status, item.expiresAt, now)).length,
  })).sort((left, right) => right.paidAmountMinor - left.paidAmountMinor || left.appCode.localeCompare(right.appCode));
  return {
    commerce: {
      orderCount: demoOrders.filter((order) => inRange(order.createdAt, from, to)).length,
      paidCount: demoOrders.filter((order) => inRange(order.paidAt, from, to)).length,
      refundedCount: demoOrders.filter((order) => inRange(order.refundedAt, from, to)).length,
      paidAmountMinor: demoOrders.filter((order) => inRange(order.paidAt, from, to)).reduce((sum, order) => sum + order.amountMinor, 0),
      refundedAmountMinor: demoOrders.filter((order) => inRange(order.refundedAt, from, to)).reduce((sum, order) => sum + order.amountMinor, 0),
      includesSandbox: true,
      sandboxOrderCount: 0,
      sandboxPaidAmountMinor: 0,
    },
    membership: {
      active: demoMemberships.filter((item) => isMembershipActive(item.status, item.expiresAt, now)).length,
      granted: demoMemberships.filter((item) => inRange(item.startedAt, from, to)).length,
      expired: demoMemberships.filter((item) => item.status === "expired" || (item.status === "active" && !isMembershipActive(item.status, item.expiresAt, now))).length,
      canceled: demoMemberships.filter((item) => inRange(item.canceledAt, from, to)).length,
    },
    apps,
    series: eachDay(from, to).map((date) => ({
      date,
      newUsers: 0,
      paidOrders: demoOrders.filter((order) => order.paidAt !== null && shanghaiDay(order.paidAt) === date).length,
      paidAmountMinor: demoOrders
        .filter((order) => order.paidAt !== null && shanghaiDay(order.paidAt) === date)
        .reduce((sum, order) => sum + order.amountMinor, 0),
    })),
  };
}

export function createDevelopmentDashboardApi(options: { initialTargets?: readonly TargetPeriod[] } = {}): DashboardApi {
  const periods = new Map<string, TargetPeriod>();
  const key = (query: DashboardQuery) => query.dateFrom + ":" + query.dateTo;
  for (const item of options.initialTargets ?? []) {
    const snapshot = targetPeriodSchema.parse(item);
    periods.set(key({ dateFrom: snapshot.periodStart, dateTo: snapshot.periodEnd }), structuredClone(snapshot));
  }
  const snapshotFor = (query: DashboardQuery): TargetPeriod => periods.get(key(query)) ?? {
    periodStart: query.dateFrom, periodEnd: query.dateTo, version: 0, userIds: [],
  };
  return { async getSummary(input) {
    const q = dashboardQuerySchema.parse(input);
    const targetUsers = snapshotFor(q).userIds.length;
    const cockpit = summarizeDevelopmentCockpit(q);
    return {
    period: { from: q.dateFrom, to: q.dateTo }, generatedAt: new Date().toISOString(),
    effectiveOperations: { rate: targetUsers ? 0 : null, operatedUsers: 0, targetUsers, explanation: targetUsers ? "本地演示已保存周期目标用户，尚无可验证的运营结果；不代表生产数据。" : "本地开发未连接生产聚合数据，周期目标用户尚未配置。" },
    growth: { totalUsers: null, newUsers: null, registrationConversionRate: null, registrationConversionReason: "尚未接入注册尝试事件", sources: [] },
    activity: { dau: null, wau: null, mau: null, wauMauRatio: null, retention: { value: null, available: false, reason: "暂无留存采集", day7: { value: null, available: false, reason: "暂无留存采集" }, day30: { value: null, available: false, reason: "暂无留存采集" } } },
    identity: { unionBoundRate: null, verifiedRate: null, duplicates: null },
    quality: { phoneRate: null, emailRate: null, tagCoverageRate: null, sourceCompletenessRate: null },
    risk: { highRisk: null, abnormalEvents: null, avgResolutionHours: null, avgResolutionReason: "风险事件尚未记录开始处置时间" },
    compliance: { pendingDeletion: null, deletionSlaRate: null, deletionSlaReason: "尚未配置注销完成时限及计时规则；驳回不视为注销完成" },
    reliability: { available: false, requestCount: 0, p95LatencyMs: 0, errorRate: null, auditSuccessRate: null, freshAt: null, reason: "尚未接入性能、错误率及审计投递遥测" },
    ...cockpit,
  }; }, async listTargets(input) {
    const q = dashboardQuerySchema.parse(input);
    return structuredClone(snapshotFor(q));
  }, async mutateTargets(input) {
    const parsed = targetMutationSchema.safeParse(input);
    if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", "请检查目标周期及变更用户，单次变更最多 5000 位");
    const value = parsed.data;
    const query = { dateFrom: value.dateFrom, dateTo: value.dateTo };
    const current = snapshotFor(query);
    if (current.version !== value.expectedVersion) throw new M1ApiError("DASHBOARD_TARGET_VERSION_CONFLICT", "目标用户已被其他管理员更新，请重新加载后再保存");
    const original = new Set(current.userIds);
    const selected = new Set(original);
    value.removeUserIds.forEach(id => selected.delete(id));
    value.addUserIds.forEach(id => selected.add(id));
    const userIds = [...selected].sort();
    if (userIds.length === original.size && userIds.every(id => original.has(id))) return structuredClone(current);
    if (current.version >= 2147483647) throw new M1ApiError("VALIDATION_ERROR", "目标版本已达到上限，请联系管理员");
    const next = { ...current, userIds, version: current.version + 1 };
    periods.set(key(query), next);
    return structuredClone(next);
  } };
}
